import type { MailMessageActionState } from 'hooks/useMailMessageActions'
import { conversationSourceId, conversationMailboxId, conversationSortAt } from 'utils/mailConversationPresentation'
import type { MailConversationSummary, MailConversationBody } from 'utils/mailConversationPresentation'
import { useId, ReactNode } from 'react'
import { Button } from '../ui/button'
import { addressLabel } from '../../services/mail'
import type { MailMessage, MailMessageDetail } from '../../services/mail'
import { MailMessageAttachments } from './MailMessageAttachments'
import type { MailAttachmentPresentation } from './MailMessageAttachments'

// Internal presentation contract only. Not a claim about the unimplemented logical API.
export type MailBodyPresentation =
  | { status: 'idle' | 'loading' }
  | { status: 'ready'; detail: MailConversationBody }
  | { status: 'error'; message: string }
  | { status: 'unavailable'; reason: string }

export interface MailConversationMessageProps {
  attachmentStates?: Record<string, MailMessageActionState>
  actions?: ReactNode
  logicalMessageId: string
  summary: MailConversationSummary
  conversationSubject: string
  expanded: boolean
  body: MailBodyPresentation
  preview?: string
  sentByUser?: boolean
  attachments?: MailAttachmentPresentation[]
  onToggle: (logicalMessageId: string) => void
  onRetry?: (logicalMessageId: string, sourceId: string) => void
  onDownload?: (logicalMessageId: string, sourceId: string, attachmentId: string) => void
  onExport?: (logicalMessageId: string, sourceId: string, attachmentId: string) => void
}

export function MailConversationMessage({ attachmentStates, actions, logicalMessageId, summary, conversationSubject, expanded, body,
  preview, sentByUser, attachments = [], onToggle, onRetry, onDownload, onExport }: MailConversationMessageProps) {
  const instance = useId()
  const base = `mail-message-${encodeURIComponent(logicalMessageId)}-${instance}`
  // A stale source body must never remain visible or actionable after source replacement.
  const detail = body.status === 'ready' && conversationSourceId(body.detail) === conversationSourceId(summary) && conversationMailboxId(body.detail) === conversationMailboxId(summary)
    && body.detail.availability === summary.availability && summary.availability !== 'expired'
    ? body.detail : undefined
  const date = conversationSortAt(summary)
  const parsedDate = new Date(date)
  const dateLabel = Number.isNaN(parsedDate.getTime()) ? 'Data non disponibile' : parsedDate.toLocaleString('it-IT')
  return <article data-mail-logical-id={logicalMessageId} className="mail-conversation-message" aria-labelledby={`${base}-toggle`}>
    <header>
      <h3><Button type="button" variant="ghost" id={`${base}-toggle`} className="mail-conversation-toggle"
        aria-expanded={expanded} aria-controls={`${base}-content`} onClick={() => onToggle(logicalMessageId)}>
        <span>{summary.sender.map(addressLabel).join(', ') || 'Mittente non disponibile'}</span>
        <span>{expanded ? 'Comprimi' : 'Espandi'} messaggio</span>
      </Button></h3>
      {sentByUser === true && <p>Inviato da te</p>}
      <time dateTime={Number.isNaN(parsedDate.getTime()) ? undefined : date}>{dateLabel}</time>
      {summary.subject !== conversationSubject && <p className="mail-conversation-subject">{summary.subject || '(Senza oggetto)'}</p>}
      <details className="mail-source-details"><summary>Destinatari del messaggio</summary>
        <p>A: {summary.to.map(addressLabel).join(', ') || 'Nessuno'}</p>
        {!!summary.cc.length && <p>Cc: {summary.cc.map(addressLabel).join(', ')}</p>}
      </details>
      {summary.availability !== 'complete' && <div className="mail-availability">
        <p>{{ partial: 'Fonte parziale', metadata_only: 'Solo metadati', expired: 'Fonte scaduta', complete: 'Fonte completa' }[summary.availability]}</p>
        {!!summary.incomplete_reasons.length && <details><summary>Dettagli disponibilità</summary>{summary.incomplete_reasons.map((reason, index) => <p key={`${index}-${reason}`}>{reason}</p>)}</details>}
      </div>}
    </header>
    {!expanded && preview !== undefined && <p className="mail-conversation-preview">{preview}</p>}
    <div id={`${base}-content`} hidden={!expanded}>
      {expanded && <>
        {summary.availability === 'expired' && body.status === 'ready' && <p>Corpo non disponibile: fonte scaduta.</p>}
        {(body.status === 'idle' || (body.status === 'ready' && !detail && summary.availability !== 'expired')) && <p>Corpo non caricato.</p>}
        {body.status === 'loading' && <p role="status">Caricamento del messaggio…</p>}
        {body.status === 'unavailable' && <p>{body.reason}</p>}
        {body.status === 'error' && <div role="alert"><p>{body.message}</p>
          {onRetry && <Button type="button" variant="outline" onClick={() => onRetry(logicalMessageId, conversationSourceId(summary))}
            aria-label={`Riprova corpo del messaggio ${logicalMessageId}`}>Riprova</Button>}
        </div>}
        {detail && <>
          <div className="mail-conversation-body" aria-label="Testo del messaggio">{detail.body_text === '' && detail.availability !== 'complete' ? <p>Testo del messaggio non disponibile.</p> : detail.body_text === '' ? <p>Il corpo del messaggio è vuoto.</p> : detail.body_text}</div>
          <details className="mail-source-details"><summary>Dettagli della fonte</summary>
            <p>Fonte: {conversationSourceId(detail)}</p><p>Disponibilità: {detail.availability}</p><p>SHA-256: {'source_sha256' in detail ? detail.source_sha256 || 'Non disponibile' : 'Non caricato'}</p>
            {'remote_presences' in detail && detail.remote_presences.map(presence => <p key={presence.id}>{presence.folder}: {presence.is_present ? 'Presente' : 'Non presente'}</p>)}
          </details>
          {actions}
          <MailMessageAttachments states={attachmentStates} logicalMessageId={logicalMessageId} sourceId={conversationSourceId(detail)}
            items={attachments.flatMap(item => {
              const current = detail.attachments.find(attachment => attachment.id === item.attachment.id)
              return current ? [{ ...item, attachment: current }] : []
            })}
            onDownload={onDownload} onExport={onExport} />
        </>}
      </>}
    </div>
  </article>
}
